import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader, StatusBadge, DEPARTMENTS } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, Briefcase, MapPin, Users2, Trash2, Lock, Unlock, Send } from "lucide-react";
import { toast } from "sonner";

const JOB_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const STAGES = ["Applied", "Shortlisted", "Interview", "Selected", "Rejected"];
const emptyJob = { title: "", department: "Engineering", location: "", job_type: "Full-time", description: "", requirements: "" };

function ApplicationsDialog({ job, onClose }) {
  const qc = useQueryClient();
  const { data: apps = [], isLoading } = useQuery({
    queryKey: ["job-applications", job?.id],
    queryFn: async () => (await api.get(`/jobs/${job.id}/applications`)).data,
    enabled: !!job,
  });
  const decideMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/applications/${id}`, { status })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["job-applications", job.id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success(d?.status_emailed ? `Marked ${d.status} — applicant notified` : `Marked ${d.status}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="applications-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Users2 className="w-4 h-4 text-accent" /> Applicants — {job?.title}</DialogTitle>
          <DialogDescription>Review internal applicants and move them through the hiring stages. The applicant is emailed on each status change.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : apps.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No applications yet for this posting.</p>
        ) : (
          <div className="overflow-x-auto max-h-[60vh]">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Applicant</TableHead><TableHead>Note</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Move to</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id} data-testid={`application-row-${a.id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{a.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{a.employee_id} {a.current_designation && `• ${a.current_designation}`}</p>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{a.cover_note || "—"}</TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                    <TableCell className="text-right">
                      <Select value={a.status} onValueChange={(status) => decideMut.mutate({ id: a.id, status })}>
                        <SelectTrigger className="h-8 w-[130px] ml-auto text-xs" data-testid={`application-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ job, onClose }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const applyMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${job.id}/apply`, { cover_note: note })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["my-applications"] }); onClose(); setNote(""); toast.success("Application submitted"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Apply — {job?.title}</DialogTitle>
          <DialogDescription>{job?.department} • {job?.job_type}{job?.location && ` • ${job.location}`}. Add a short note on why you're a fit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label>Why you're interested (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Your relevant experience and motivation" data-testid="apply-note-input" />
        </div>
        <DialogFooter>
          <Button onClick={() => applyMut.mutate()} disabled={applyMut.isPending} className="rounded-lg" data-testid="apply-submit-button">
            {applyMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Submit Application</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InternalJobs() {
  const { role } = useOutletContext();
  const isHR = role === "hr";
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyJob);
  const [appsFor, setAppsFor] = useState(null);
  const [applyFor, setApplyFor] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data,
  });
  const { data: myApps = [] } = useQuery({
    queryKey: ["my-applications"], queryFn: async () => (await api.get("/applications/me")).data,
    enabled: !isHR,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/jobs", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); setOpen(false); setForm(emptyJob); toast.success("Job posting published"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const toggleMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/jobs/${id}`, { status })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success(`Posting ${d.status === "Open" ? "reopened" : "closed"}`); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/jobs/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success("Posting deleted"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Internal Job Postings" subtitle={isHR ? "Publish internal openings and review employee applications." : "Explore open roles within Skanovix and apply."}>
        {isHR && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-lg font-semibold active:scale-[0.98]" data-testid="post-job-button">
                <Plus className="w-4 h-4 mr-1.5" /> Post a Job
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="post-job-dialog">
              <DialogHeader><DialogTitle className="font-heading">New Internal Opening</DialogTitle><DialogDescription>Visible to all employees while the posting is Open.</DialogDescription></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Job Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Senior Backend Engineer" data-testid="job-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => set("department", v)}>
                      <SelectTrigger data-testid="job-department-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Employment Type</Label>
                    <Select value={form.job_type} onValueChange={(v) => set("job_type", v)}>
                      <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{JOB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Visakhapatnam / Remote" data-testid="job-location-input" /></div>
                <div className="space-y-1.5"><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Role overview and responsibilities" data-testid="job-description-input" /></div>
                <div className="space-y-1.5"><Label>Requirements (optional)</Label><Textarea value={form.requirements} onChange={(e) => set("requirements", e.target.value)} placeholder="Skills and experience expected" data-testid="job-requirements-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.title.trim() || !form.description.trim()} className="rounded-lg" data-testid="job-submit-button">
                  {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish Posting"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : jobs.length === 0 ? (
        <Card className="p-12 border border-dashed border-border rounded-xl text-center" data-testid="jobs-empty">
          <Briefcase className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">{isHR ? "No internal openings yet. Post your first job above." : "No open positions right now. Check back soon!"}</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {jobs.map((j) => (
            <Card key={j.id} className={`p-5 border rounded-xl flex flex-col ${j.status === "Closed" ? "border-border opacity-70" : "border-border hover:shadow-md"} transition-shadow`} data-testid={`job-card-${j.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-base leading-tight">{j.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{j.department}</p>
                </div>
                <StatusBadge status={j.status === "Open" ? "Active" : "Rejected"} />
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 rounded-full"><Briefcase className="w-3 h-3" /> {j.job_type}</span>
                {j.location && <span className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 rounded-full"><MapPin className="w-3 h-3" /> {j.location}</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-3 flex-1">{j.description}</p>
              {j.requirements && <p className="text-xs text-muted-foreground mt-2 line-clamp-2"><span className="font-semibold text-foreground">Requirements: </span>{j.requirements}</p>}

              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2">
                {isHR ? (
                  <>
                    <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={() => setAppsFor(j)} data-testid={`view-applicants-${j.id}`}>
                      <Users2 className="w-4 h-4 mr-1.5" /> {j.application_count || 0} applicant{(j.application_count || 0) === 1 ? "" : "s"}
                    </Button>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => toggleMut.mutate({ id: j.id, status: j.status === "Open" ? "Closed" : "Open" })} title={j.status === "Open" ? "Close posting" : "Reopen posting"} data-testid={`toggle-job-${j.id}`}>
                        {j.status === "Open" ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 text-destructive hover:bg-destructive/10" title="Delete posting" data-testid={`delete-job-${j.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid={`delete-job-dialog-${j.id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{j.title}"?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes the posting and all {j.application_count || 0} application(s). This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`delete-job-cancel-${j.id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMut.mutate(j.id)} className="bg-destructive hover:bg-destructive/90" data-testid={`delete-job-confirm-${j.id}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </>
                ) : j.my_application_status ? (
                  <div className="w-full flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Your application</span>
                    <StatusBadge status={j.my_application_status} />
                  </div>
                ) : (
                  <Button size="sm" className="h-8 rounded-lg w-full" onClick={() => setApplyFor(j)} data-testid={`apply-button-${j.id}`}>
                    <Send className="w-4 h-4 mr-1.5" /> Apply
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {!isHR && myApps.length > 0 && (
        <Card className="border border-border rounded-xl overflow-hidden mt-8" data-testid="my-applications">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2"><Send className="w-4 h-4 text-accent" /><h3 className="font-heading font-semibold">My Applications</h3></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Role</TableHead><TableHead>Applied</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {myApps.map((a) => (
                  <TableRow key={a.id} data-testid={`my-application-${a.id}`}>
                    <TableCell className="font-medium">{a.job_title}</TableCell>
                    <TableCell className="font-mono text-xs">{new Date(a.applied_at).toLocaleDateString("en-IN")}</TableCell>
                    <TableCell><StatusBadge status={a.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {isHR && <ApplicationsDialog job={appsFor} onClose={() => setAppsFor(null)} />}
      {!isHR && <ApplyDialog job={applyFor} onClose={() => setApplyFor(null)} />}
    </div>
  );
}
